'use client';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

export type ScriptStatus = 'idle' | 'loading' | 'loaded' | 'error';

type ScriptState = Record<string, ScriptStatus>;

type ScriptLoaderContextType = {
  scripts: ScriptState;
  loadScript: (key: string, src: string) => Promise<void>;
  getStatus: (key: string) => ScriptStatus;
  isLoaded: (key: string) => boolean;
};

const ScriptLoaderContext = createContext<ScriptLoaderContextType | null>(null);

export function ScriptLoaderProvider({ children }: PropsWithChildren) {
  const [scripts, setScripts] = useState<ScriptState>({});
  const scriptsRef = useRef<ScriptState>({});
  const loadingPromises = useRef<Record<string, Promise<void> | undefined>>({});

  const setStatus = useCallback((key: string, status: ScriptStatus) => {
    scriptsRef.current = { ...scriptsRef.current, [key]: status };
    setScripts(scriptsRef.current);
  }, []);

  const loadScript = useCallback(
    async (key: string, src: string): Promise<void> => {
      if (scriptsRef.current[key] === 'loaded') {
        return Promise.resolve();
      }

      // 이미 로딩 중이면 기존 Promise 반환 (중복 로드 방지)
      if (loadingPromises.current[key]) {
        return loadingPromises.current[key];
      }

      const previousStatus = scriptsRef.current[key];

      const promise = new Promise<void>((resolve, reject) => {
        // SSR 체크
        if (typeof window === 'undefined') {
          reject(new Error('Cannot load script on server side'));
          return;
        }

        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          // 이전 로드가 실패했다면 남은 태그를 제거하고 새로 시도한다.
          if (previousStatus === 'error') {
            existingScript.remove();
          } else {
            setStatus(key, 'loaded');
            resolve();
            return;
          }
        }

        setStatus(key, 'loading');

        const script = document.createElement('script');
        script.src = src;
        script.async = true;

        script.onload = () => {
          setStatus(key, 'loaded');
          delete loadingPromises.current[key];
          resolve();
        };

        script.onerror = () => {
          setStatus(key, 'error');
          delete loadingPromises.current[key];
          reject(new Error(`Failed to load script: ${src}`));
        };

        document.head.appendChild(script);
      });

      loadingPromises.current[key] = promise;
      return promise;
    },
    [setStatus],
  );

  const getStatus = useCallback(
    (key: string): ScriptStatus => scripts[key] ?? 'idle',
    [scripts],
  );

  const isLoaded = useCallback(
    (key: string): boolean => scripts[key] === 'loaded',
    [scripts],
  );

  return (
    <ScriptLoaderContext.Provider
      value={{ scripts, loadScript, getStatus, isLoaded }}
    >
      {children}
    </ScriptLoaderContext.Provider>
  );
}

export function useScriptLoader() {
  const context = useContext(ScriptLoaderContext);

  if (!context) {
    throw new Error(
      'useScriptLoader must be used within a ScriptLoaderProvider',
    );
  }

  return context;
}
